// FNP-1016: the RESOURCES backing an application, as a record separate from the
// application itself.
//
// Design doc: docs/decisions/application-infrastructure-model.md
// Schema: flexinet-console-api .../schema/features/application-infrastructure.graphql
//
// These types mirror the GraphQL schema exactly. Where the schema comments carry
// a safety property, the property is restated here — the wire format is the only
// place the rule can actually be enforced, and a caller reading this file must
// not have to go find the schema to learn that omitting a field is dangerous.

// The infrastructure lifecycle, split out of `applicationStatus`.
export enum InfrastructureStatus {
    // Intent recorded, nothing built yet. The write-before-create row.
    PLANNED = 'PLANNED',
    PROVISIONING = 'PROVISIONING',
    ACTIVE = 'ACTIVE',
    DEPROVISIONING = 'DEPROVISIONING',
    DECOMMISSIONED = 'DECOMMISSIONED',
    FAILED = 'FAILED',
}

// Where the record came from — and therefore how much of it is trustworthy.
//
// ⚠️ ORCHESTRATOR is the API's DEFAULT when `origin` is omitted. Registration
// must therefore send REGISTERED explicitly; a registration that forgets it is
// silently recorded as though this platform provisioned the resources.
export enum InfrastructureOrigin {
    ORCHESTRATOR = 'ORCHESTRATOR',
    REGISTERED = 'REGISTERED',
}

// Whether the named resource is known to exist. PENDING means the row was
// written before the create call was confirmed — the resource MAY exist.
// ⚠️ Never read PENDING as absence.
export enum ResourceConfirmation {
    PENDING = 'PENDING',
    CONFIRMED = 'CONFIRMED',
}

// ⚠️ PER-STAGE, AND A SPARSE ARRAY — not a {dev,test,prod} map.
//
// AN ABSENT STAGE MEANS "NOT PROVISIONED". Padding the array with empty-string
// entries to "fill in" the missing stages recreates exactly the ambiguity this
// entity exists to remove: live prod data already carries
// `infraStackNames = {dev:"", test:"", prod:"…"}`, where the empty strings are
// indistinguishable from never-written.
export interface ApplicationInfrastructureStage {
    stage: string;
    // ⚠️ accountId and stage travel together as one structural unit. This is
    // where the account→stage inference dies.
    accountId: string;
    region: string;
    // ⚠️ NON-NULL BY DESIGN. `ValidateDeprovisionable` hard-refuses a missing
    // stack name, because a stack pipelines-api cannot FIND is reported as
    // DELETE_COMPLETE — so a wrong/absent name reads as a successful deletion
    // and silently orphans live infrastructure. A stage entry with no stack
    // name is not a stage entry; omit the stage instead.
    infraStackName: string;
    deploymentBucketName?: string | null;
    domain?: string | null;
    hostedZoneId?: string | null;
    certificateArn?: string | null;
}

export interface ApplicationInfrastructure {
    id: string;
    applicationId: string;

    // ── SHARED ACROSS ALL STAGES ────────────────────────────────────────────
    sourceRepositoryName?: string | null;
    infraRepositoryName?: string | null;
    sourcePipelineId?: string | null;
    infraPipelineId?: string | null;
    deploymentAccountGroupId?: string | null;

    // ── PER-STAGE, SPARSE ───────────────────────────────────────────────────
    stages: ApplicationInfrastructureStage[];

    infrastructureStatus: InfrastructureStatus;

    infraRepositoryConfirmation?: ResourceConfirmation | null;
    sourceRepositoryConfirmation?: ResourceConfirmation | null;
    infraPipelineConfirmation?: ResourceConfirmation | null;
    sourcePipelineConfirmation?: ResourceConfirmation | null;

    provisioningExecutionId?: string | null;

    origin: InfrastructureOrigin;

    // ⚠️ REFUSES TEARDOWN. Defaults to TRUE for REGISTERED records — the
    // resolver derives it from `origin`, so the caller does not have to
    // remember to ask for protection.
    protected: boolean;

    created?: string | null;
    createdByUsername?: string | null;
    updated?: string | null;
    updatedByUsername?: string | null;
}

export interface ApplicationInfrastructureConnection {
    items: ApplicationInfrastructure[];
    nextToken: string | null;
}

// The wire input for a stage. Identical in shape to the read type; kept
// separate so a future divergence in the schema does not silently typecheck.
export interface ApplicationInfrastructureStageInput {
    stage: string;
    accountId: string;
    region: string;
    infraStackName: string;
    deploymentBucketName?: string;
    domain?: string;
    hostedZoneId?: string;
    certificateArn?: string;
}

export interface CreateApplicationInfrastructureInput {
    applicationId: string;
    sourceRepositoryName?: string;
    infraRepositoryName?: string;
    sourcePipelineId?: string;
    infraPipelineId?: string;
    deploymentAccountGroupId?: string;
    stages?: ApplicationInfrastructureStageInput[];
    infrastructureStatus?: InfrastructureStatus;
    provisioningExecutionId?: string;
    // ⚠️ Defaults to ORCHESTRATOR when omitted — registration MUST send
    // REGISTERED.
    origin?: InfrastructureOrigin;
    // ⚠️ Deliberately NOT set by the registration form. Omitting it yields
    // `true` for a REGISTERED record, which is the safe outcome; sending it
    // would let a form arm a teardown of a live production system.
    protected?: boolean;
}

export interface UpdateApplicationInfrastructureInput {
    id: string;
    sourceRepositoryName?: string;
    infraRepositoryName?: string;
    sourcePipelineId?: string;
    infraPipelineId?: string;
    deploymentAccountGroupId?: string;
    // ⚠️ REPLACES the array wholesale. There is no per-stage merge — callers
    // read, amend, and write the WHOLE array.
    stages?: ApplicationInfrastructureStageInput[];
    infrastructureStatus?: InfrastructureStatus;
    provisioningExecutionId?: string;
    protected?: boolean;
}

// The form's own shape. Distinct from the wire input because a form field is
// always a string — the empty strings live HERE and are stripped on the way
// out, never sent.
export interface RegisterInfrastructureStageValues {
    stage: string;
    accountId: string;
    region: string;
    infraStackName: string;
    deploymentBucketName: string;
    domain: string;
    hostedZoneId: string;
    certificateArn: string;
}

// A blank stage row, as the form models it. Lives here rather than beside the
// FieldArray component so the component file exports only components.
//
// ⚠️ These empty strings never reach the wire — `toStageInput` drops a row that
// cannot satisfy the required quartet, and omits every blank optional field.
export const EMPTY_STAGE_VALUES: RegisterInfrastructureStageValues = {
    stage: '',
    accountId: '',
    region: '',
    infraStackName: '',
    deploymentBucketName: '',
    domain: '',
    hostedZoneId: '',
    certificateArn: '',
};

export interface RegisterInfrastructureFormValues {
    sourceRepositoryName: string;
    infraRepositoryName: string;
    sourcePipelineId: string;
    infraPipelineId: string;
    deploymentAccountGroupId: string;
    stages: RegisterInfrastructureStageValues[];
}
